import { motion } from "framer-motion";
import { Database, Users2, Target, Activity } from "lucide-react";

const CAPABILITIES = [
    {
        icon: Database,
        title: "Voter Files & Targeting",
        desc: "The same voter files and modeled scores trusted by winning campaigns nationwide.",
    },
    {
        icon: Users2,
        title: "Volunteer & CRM",
        desc: "Organize volunteers, assign shifts, and track every voter conversation in one place.",
    },
    {
        icon: Target,
        title: "Universe Building",
        desc: "Persuasion, GOTV, and ID universes built around your actual path to victory.",
    },
    {
        icon: Activity,
        title: "Performance Analytics",
        desc: "Daily field metrics, contact rates, and post-program analysis you can actually act on.",
    },
];

export default function ModernTools() {
    return (
        <section
            id="tools"
            data-testid="tools-section"
            className="relative py-28 md:py-36 bg-ink overflow-hidden"
        >
            <div className="absolute top-0 inset-x-0 h-px hairline-gold" />
            <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(700px_400px_at_80%_20%,rgba(201,162,39,0.08),transparent_65%)]" />

            <div className="mx-auto max-w-7xl px-6 md:px-10 relative">
                {/* Headline row */}
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-end mb-16">
                    <div className="lg:col-span-7">
                        <div className="flex items-center gap-3 mb-5">
                            <span className="h-px w-8 bg-gold" />
                            <span className="text-[10px] uppercase tracking-[0.4em] text-gold font-semibold">
                                04 / Modern Campaign Tools
                            </span>
                        </div>
                        <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
                            Successful campaigns{" "}
                            <span className="italic text-gold">run on data.</span>
                        </h2>
                    </div>
                    <p className="lg:col-span-5 text-cream/65 text-base md:text-lg leading-relaxed">
                        We use the same voter contact, CRM, and campaign management
                        tools trusted by winning campaigns across the country —
                        organizing volunteers, tracking conversations, identifying
                        supporters, and maximizing every dollar you spend.
                    </p>
                </div>

                {/* Stats counter strip */}
                <div
                    data-testid="tools-output-stats"
                    className="grid grid-cols-1 md:grid-cols-3 gap-px bg-gold/20 border border-gold/20 mb-16"
                >
                    {[
                        {
                            v: "Tens of Thousands",
                            k: "Doors Knocked",
                            sub: "Across local, county, legislative, and statewide programs",
                        },
                        {
                            v: "Hundreds of Thousands",
                            k: "Phone Calls Made",
                            sub: "Predictive dialer and volunteer phone bank operations",
                        },
                        {
                            v: "Millions",
                            k: "Text Messages Sent",
                            sub: "Peer-to-peer text programs with tracked responses",
                        },
                    ].map((s, i) => (
                        <motion.div
                            key={s.k}
                            initial={{ opacity: 0, y: 14 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.08 }}
                            data-testid={`output-stat-${i}`}
                            className="bg-[#070707] px-6 py-8 md:px-8 md:py-10"
                        >
                            <div className="font-display text-3xl md:text-4xl text-gold leading-tight">
                                {s.v}
                            </div>
                            <div className="mt-2 text-[10px] uppercase tracking-[0.3em] text-cream/80">
                                {s.k}
                            </div>
                            <div className="mt-4 text-xs md:text-sm text-cream/55 leading-relaxed">
                                {s.sub}
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Capabilities */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-gold/15 border border-gold/15">
                    {CAPABILITIES.map((c, i) => {
                        const Icon = c.icon;
                        return (
                            <motion.div
                                key={c.title}
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.06 }}
                                data-testid={`tool-card-${i}`}
                                className="bg-[#080808] p-7 md:p-8"
                            >
                                <Icon
                                    size={20}
                                    className="text-gold mb-5"
                                    strokeWidth={1.6}
                                />
                                <div className="font-display text-xl md:text-2xl text-cream mb-2 leading-tight">
                                    {c.title}
                                </div>
                                <div className="text-cream/60 text-sm leading-relaxed">
                                    {c.desc}
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
